import React, { RefObject } from 'react'
import styled from 'styled-components'
import { Vector2 } from 'three'

const DragContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: max-content;
  width: max-content;
  cursor: grab;
`

const DragBorder = styled.div`
  display: none;
  position: absolute;
  left: -50px;
  top: -50px;
  width: calc(100% + 100px);
  height: calc(100% + 100px);
`

interface DraggableProps {
  children: React.ReactNode
  onDoubleClick?: () => void
  style?: React.CSSProperties
  getDraggableGroup?: () => RefObject<HTMLElement | null>[]
  groupZIndex?: number
  /** styled-components uses the classname field to apply css to components -- this allows someone to do styled(Draggable)`` */
  className?: string
  sharedRef?: React.RefObject<HTMLDivElement>
}

export const Draggable: React.FC<
  DraggableProps & React.HTMLAttributes<HTMLDivElement>
> = (props) => {
  const mouseDownInside = React.useRef<boolean>(false)
  const positionAtMouseDown = React.useRef<Vector2>(new Vector2(0, 0))
  const mouseDownPosition = React.useRef<Vector2>(new Vector2(0, 0))
  const fallbackRef = React.useRef<HTMLDivElement>(null)
  const draggableRef = props.sharedRef ?? fallbackRef
  const dragBorderRef = React.useRef<HTMLDivElement>(null)

  const reorderStacking = !props.getDraggableGroup
    ? undefined
    : (currentEl: HTMLDivElement) => {
        props.getDraggableGroup!()
          .filter((draggable) => draggable.current)
          .map((draggable) => ({
            ref: draggable,
            zIndex:
              draggable!.current!.parentElement === currentEl
                ? Infinity
                : parseInt(
                    draggable.current!.parentElement!.style.zIndex || '0',
                    10
                  ),
          }))
          .sort((a, b) => a.zIndex - b.zIndex)
          .forEach((draggable, index) => {
            draggable!.ref.current!.parentElement!.style.zIndex = (
              (props.groupZIndex ?? 0) + index
            ).toString()
          })
      }

  const onMouseDown = (e: React.MouseEvent) => {
    if (!draggableRef.current || !dragBorderRef.current) return
    e.preventDefault()
    e.stopPropagation()
    mouseDownInside.current = true
    dragBorderRef.current.style.display = 'block'
    draggableRef.current.style.cursor = 'grabbing'
    positionAtMouseDown.current.x =
      parseInt(draggableRef.current.style.left) || 0
    positionAtMouseDown.current.y =
      parseInt(draggableRef.current.style.top) || 0
    mouseDownPosition.current.x = e.clientX
    mouseDownPosition.current.y = e.clientY
    if (reorderStacking) reorderStacking(draggableRef.current)
  }

  const onMouseMove = (e: MouseEvent) => {
    if (mouseDownInside.current && draggableRef.current) {
      e.preventDefault()
      e.stopPropagation()
      draggableRef.current.style.left =
        positionAtMouseDown.current.x +
        e.clientX -
        mouseDownPosition.current.x +
        'px'
      draggableRef.current.style.top =
        positionAtMouseDown.current.y +
        e.clientY -
        mouseDownPosition.current.y +
        'px'
    }
  }

  const onMouseUp = (e: MouseEvent) => {
    if (
      mouseDownInside.current &&
      draggableRef.current &&
      dragBorderRef.current
    ) {
      e.preventDefault()
      e.stopPropagation()
      mouseDownInside.current = false
      draggableRef.current.style.cursor = 'grab'
      dragBorderRef.current.style.display = 'none'
    }
  }

  React.useEffect(() => {
    document.body.addEventListener('mousemove', onMouseMove)
    document.body.addEventListener('mouseup', onMouseUp)
    document.body.addEventListener('mouseleave', onMouseUp)
    return () => {
      document.body.removeEventListener('mousemove', onMouseMove)
      document.body.removeEventListener('mouseup', onMouseUp)
      document.body.removeEventListener('mouseleave', onMouseUp)
    }
  })

  return (
    <DragContainer
      style={{
        ...(props.style ?? {}),
      }}
      className={props.className}
      ref={draggableRef}
      onMouseDown={onMouseDown}
      onDoubleClick={() => props.onDoubleClick && props.onDoubleClick()}>
      <DragBorder ref={dragBorderRef} />
      {props.children}
    </DragContainer>
  )
}
